import { useState, useMemo, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Check, Minus, Plus, Pizza, Layers, UtensilsCrossed, AlertTriangle, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import type { InternalComboWithDetails, ComboSlotWithProducts } from "@/hooks/useInternalCombos";
import type { ComboSelection, SlotSelection } from "@/hooks/useCart";

interface ComboDetailSheetProps {
  combo: InternalComboWithDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddToCart: (
    combo: InternalComboWithDetails,
    selection: ComboSelection,
    calculatedPrice: number,
    quantity: number,
    notes?: string
  ) => void;
}

type SlotSelectionMap = Map<string, { productId: string; productName: string; price: number }[]>;

export function ComboDetailSheet({
  combo,
  open,
  onOpenChange,
  onAddToCart,
}: ComboDetailSheetProps) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  
  // Legacy: Pizza flavor selection
  const [selectedFlavors, setSelectedFlavors] = useState<{
    productId: string;
    productName: string;
    price: number;
  }[]>([]);

  // Legacy: Normal combo choice selection
  const [selectedChoices, setSelectedChoices] = useState<Map<string, {
    productId: string;
    productName: string;
  }>>(new Map());

  // Modern: Slot selections
  const [slotSelections, setSlotSelections] = useState<SlotSelectionMap>(new Map());

  // Reset state when combo changes
  useEffect(() => {
    if (combo) {
      setQuantity(1);
      setNotes("");
      setSelectedFlavors([]);
      setSelectedChoices(new Map());
      setSlotSelections(new Map());
    }
  }, [combo?.id]);

  // Early return after all hooks
  if (!combo) return null;

  // Determine which model to use
  const useSlotModel = combo.useSlotModel && combo.slots && combo.slots.length > 0;

  // Check for misconfigured combos (legacy model)
  const isPizzaMissingFlavors = !useSlotModel && combo.combo_type === 'pizza' && (!combo.pizzaFlavors || combo.pizzaFlavors.length === 0);
  const isNormalMissingItems = !useSlotModel && combo.combo_type === 'normal' && (!combo.items || combo.items.length === 0);
  const isNotConfigured = !useSlotModel && (isPizzaMissingFlavors || isNormalMissingItems);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const maxFlavors = combo.pizza_max_flavors || 1;

  // Legacy: Toggle pizza flavor
  const toggleFlavor = (flavor: { productId: string; productName: string; price: number }) => {
    setSelectedFlavors((current) => {
      const exists = current.some((f) => f.productId === flavor.productId);
      if (exists) {
        return current.filter((f) => f.productId !== flavor.productId);
      }
      if (current.length >= maxFlavors) {
        return [...current.slice(1), flavor];
      }
      return [...current, flavor];
    });
  };

  // Legacy: Select normal combo choice
  const selectChoice = (comboItemId: string, productId: string, productName: string) => {
    setSelectedChoices((current) => {
      const updated = new Map(current);
      updated.set(comboItemId, { productId, productName });
      return updated;
    });
  };

  // Modern: Handle slot product toggle
  const handleSlotProductToggle = (
    slot: ComboSlotWithProducts,
    product: { id: string; name: string; price: number }
  ) => {
    setSlotSelections(current => {
      const newMap = new Map(current);
      const currentSelections = newMap.get(slot.id) || [];
      const existingIndex = currentSelections.findIndex(s => s.productId === product.id);

      if (existingIndex >= 0) {
        const updated = currentSelections.filter(s => s.productId !== product.id);
        newMap.set(slot.id, updated);
      } else {
        if (currentSelections.length < slot.max_quantity) {
          newMap.set(slot.id, [
            ...currentSelections,
            { productId: product.id, productName: product.name, price: product.price }
          ]);
        } else if (slot.max_quantity === 1) {
          newMap.set(slot.id, [
            { productId: product.id, productName: product.name, price: product.price }
          ]);
        }
      }

      return newMap;
    });
  };

  // Calculate price based on selections
  const calculatedPrice = useMemo(() => {
    if (useSlotModel && combo.slots) {
      let total = 0;
      let hasCalculatedSlots = false;

      for (const slot of combo.slots) {
        const selections = slotSelections.get(slot.id) || [];

        // Handle fixed product slots
        if (slot.slot_type === 'product' && slot.product) {
          if (slot.pricing_rule === 'fixed' && slot.fixed_price !== null) {
            total += slot.fixed_price;
          } else {
            total += slot.product.price;
          }
          hasCalculatedSlots = true;
          continue;
        }

        // Handle category slots with selections
        if (selections.length > 0) {
          const prices = selections.map(s => s.price);
          hasCalculatedSlots = true;

          switch (slot.pricing_rule) {
            case 'highest':
              total += Math.max(...prices);
              break;
            case 'average':
              total += prices.reduce((sum, p) => sum + p, 0) / prices.length;
              break;
            case 'fixed':
              total += (slot.fixed_price ?? 0) * selections.length;
              break;
            case 'rodizio':
              // Rodizio uses base price
              break;
          }
        }
      }

      return hasCalculatedSlots ? total : Number(combo.price);
    }

    // Legacy pizza pricing
    if (combo.combo_type === 'pizza' && selectedFlavors.length > 0) {
      const prices = selectedFlavors.map((f) => f.price);
      if (combo.pizza_pricing_rule === 'highest') {
        return Math.max(...prices);
      } else if (combo.pizza_pricing_rule === 'average') {
        return prices.reduce((sum, p) => sum + p, 0) / prices.length;
      }
    }
    return Number(combo.price);
  }, [combo, selectedFlavors, slotSelections, useSlotModel]);

  // Check if selection is valid
  const isValid = useMemo(() => {
    if (isNotConfigured) return false;

    if (useSlotModel && combo.slots) {
      for (const slot of combo.slots) {
        if (slot.slot_type === 'product') continue;

        const selections = slotSelections.get(slot.id) || [];
        const count = selections.length;

        if (slot.is_required && count < slot.min_quantity) {
          return false;
        }

        if (count > slot.max_quantity) {
          return false;
        }
      }
      return true;
    }

    // Legacy validation
    if (combo.combo_type === 'pizza') {
      return selectedFlavors.length > 0 && selectedFlavors.length <= maxFlavors;
    }
    if (combo.combo_type === 'normal') {
      const requiredChoiceItems = combo.items?.filter(
        (item) => item.item_type === 'choice' && item.is_required
      ) || [];
      return requiredChoiceItems.every((item) => selectedChoices.has(item.id));
    }
    return true;
  }, [combo, selectedFlavors, selectedChoices, slotSelections, maxFlavors, isNotConfigured, useSlotModel]);

  const handleAddToCart = () => {
    const selection: ComboSelection = {};

    if (useSlotModel) {
      selection.slotSelections = Array.from(slotSelections.entries()).map(([slotId, products]) => {
        const slot = combo.slots?.find(s => s.id === slotId);
        return {
          slotId,
          slotLabel: slot?.label || slot?.category?.name || 'Escolha',
          selectedProducts: products.map(p => ({
            productId: p.productId,
            productName: p.productName,
            price: p.price,
          })),
        };
      });
    } else if (combo.combo_type === 'pizza') {
      selection.selectedFlavors = selectedFlavors;
    } else if (combo.combo_type === 'normal') {
      selection.selectedChoices = [];
      for (const item of combo.items || []) {
        if (item.item_type === 'choice') {
          const choice = selectedChoices.get(item.id);
          if (choice) {
            selection.selectedChoices.push({
              comboItemId: item.id,
              choiceLabel: item.choice_label || '',
              productId: choice.productId,
              productName: choice.productName,
            });
          }
        }
      }
    }

    onAddToCart(combo, selection, calculatedPrice, quantity, notes || undefined);
    onOpenChange(false);
  };

  const getComboIcon = () => {
    if (useSlotModel) return <Package className="h-5 w-5" />;
    switch (combo.combo_type) {
      case 'pizza':
        return <Pizza className="h-5 w-5" />;
      case 'rodizio':
        return <UtensilsCrossed className="h-5 w-5" />;
      default:
        return <Layers className="h-5 w-5" />;
    }
  };

  const getComboTypeBadge = () => {
    if (useSlotModel) return <Badge variant="secondary">Combo</Badge>;
    switch (combo.combo_type) {
      case 'pizza':
        return <Badge variant="secondary">Pizza</Badge>;
      case 'rodizio':
        return <Badge className="bg-orange-500">Rodízio</Badge>;
      default:
        return <Badge variant="outline">Combo</Badge>;
    }
  };

  const getPricingRuleLabel = (rule: string) => {
    switch (rule) {
      case 'highest': return 'Maior valor';
      case 'average': return 'Valor médio';
      case 'fixed': return 'Preço fixo';
      case 'rodizio': return 'Rodízio';
      default: return rule;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="p-4 pb-0">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              {getComboIcon()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-lg">{combo.name}</SheetTitle>
                {getComboTypeBadge()}
              </div>
              {combo.description && (
                <p className="text-sm text-muted-foreground mt-1">{combo.description}</p>
              )}
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-4">
          {/* Fallback: Combo not configured */}
          {isNotConfigured && (
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                    Combo não configurado
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Este combo não possui itens configurados. Configure os slots na aba "Combos".
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* MODERN MODEL: Slot-based rendering */}
          {useSlotModel && combo.slots && (
            <div className="space-y-6">
              {combo.slots.map((slot) => (
                <div key={slot.id} className="space-y-3">
                  {/* Fixed product slot */}
                  {slot.slot_type === 'product' && slot.product && (
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <Check className="h-4 w-4 text-green-500" />
                      <Badge variant="outline">Incluído</Badge>
                      <span className="flex-1">{slot.product.name}</span>
                      {slot.pricing_rule === 'fixed' && slot.fixed_price !== null && (
                        <span className="text-sm font-medium">
                          {formatCurrency(slot.fixed_price)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Category slot - product selection */}
                  {slot.slot_type === 'category' && slot.products && slot.products.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">
                            {slot.label || slot.category?.name || 'Escolha'}
                          </h3>
                          {slot.is_required && (
                            <Badge variant="destructive" className="text-xs">
                              Obrigatório
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {getPricingRuleLabel(slot.pricing_rule)}
                          </Badge>
                          <Badge>
                            {slotSelections.get(slot.id)?.length || 0}/{slot.max_quantity}
                          </Badge>
                        </div>
                      </div>

                      {slot.min_quantity > 0 && (
                        <p className="text-sm text-muted-foreground">
                          Escolha de {slot.min_quantity} até {slot.max_quantity} opções
                        </p>
                      )}

                      {/* Single selection - use radio */}
                      {slot.max_quantity === 1 ? (
                        <RadioGroup
                          value={slotSelections.get(slot.id)?.[0]?.productId || ''}
                          onValueChange={(value) => {
                            const product = slot.products?.find(p => p.id === value);
                            if (product) {
                              handleSlotProductToggle(slot, product);
                            }
                          }}
                        >
                          <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                            {slot.products.map((product) => (
                              <div
                                key={product.id}
                                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:border-primary/50"
                              >
                                <RadioGroupItem
                                  value={product.id}
                                  id={`slot-${slot.id}-product-${product.id}`}
                                />
                                <Label
                                  htmlFor={`slot-${slot.id}-product-${product.id}`}
                                  className="flex-1 cursor-pointer"
                                >
                                  {product.name}
                                </Label>
                                <span className="text-sm font-medium text-muted-foreground">
                                  {formatCurrency(product.price)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </RadioGroup>
                      ) : (
                        /* Multiple selection - use checkboxes */
                        <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                          {slot.products.map((product) => {
                            const isSelected = slotSelections.get(slot.id)?.some(
                              s => s.productId === product.id
                            ) || false;
                            const currentCount = slotSelections.get(slot.id)?.length || 0;
                            const canSelect = currentCount < slot.max_quantity;

                            return (
                              <div
                                key={product.id}
                                className={cn(
                                  "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                                  isSelected
                                    ? "border-primary bg-primary/5"
                                    : canSelect
                                    ? "hover:border-primary/50"
                                    : "opacity-50"
                                )}
                                onClick={() => (isSelected || canSelect) && handleSlotProductToggle(slot, product)}
                              >
                                <Checkbox checked={isSelected} />
                                <span className="flex-1 font-medium">{product.name}</span>
                                <span className="text-sm font-medium text-muted-foreground">
                                  {formatCurrency(product.price)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* LEGACY MODEL: Pizza Flavor Selection */}
          {!useSlotModel && combo.combo_type === 'pizza' && combo.pizzaFlavors && combo.pizzaFlavors.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Escolha os sabores</h3>
                <Badge variant="outline">
                  {selectedFlavors.length}/{maxFlavors}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {combo.pizza_pricing_rule === 'highest'
                  ? 'Preço do sabor mais caro'
                  : 'Média dos preços'}
              </p>
              <div className="grid grid-cols-1 gap-2">
                {combo.pizzaFlavors.map((flavor) => {
                  const isSelected = selectedFlavors.some(
                    (f) => f.productId === flavor.product_id
                  );
                  const product = flavor.product as any;
                  return (
                    <button
                      key={flavor.id}
                      onClick={() =>
                        toggleFlavor({
                          productId: flavor.product_id,
                          productName: product?.name || '',
                          price: Number(product?.price || 0),
                        })
                      }
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border text-left transition-colors",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                            isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-muted-foreground"
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                        <span className="font-medium">{product?.name}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(Number(product?.price || 0))}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* LEGACY MODEL: Normal Combo Item Selection */}
          {!useSlotModel && combo.combo_type === 'normal' && combo.items && combo.items.length > 0 && (
            <div className="space-y-4">
              {combo.items.map((item) => (
                <div key={item.id} className="space-y-2">
                  {item.item_type === 'fixed' ? (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Check className="h-4 w-4 text-green-500" />
                      <div className="flex-1">
                        <span className="font-medium">
                          {item.quantity}x {(item.product as any)?.name}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{item.choice_label || 'Escolha uma opção'}</h4>
                        {item.is_required && (
                          <Badge variant="destructive" className="text-xs">Obrigatório</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {item.options?.map((option) => {
                          const isSelected =
                            selectedChoices.get(item.id)?.productId === option.product_id;
                          const product = option.product as any;
                          return (
                            <button
                              key={option.id}
                              onClick={() =>
                                selectChoice(
                                  item.id,
                                  option.product_id,
                                  product?.name || ''
                                )
                              }
                              className={cn(
                                "flex items-center justify-between p-3 rounded-lg border text-left transition-colors",
                                isSelected
                                  ? "border-primary bg-primary/5"
                                  : "border-border hover:border-primary/50"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={cn(
                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                                    isSelected
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-muted-foreground"
                                  )}
                                >
                                  {isSelected && <Check className="h-3 w-3" />}
                                </div>
                                <span className="font-medium">{product?.name}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Rodizio Combo */}
          {!useSlotModel && combo.combo_type === 'rodizio' && (
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900">
                <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-1">
                  Rodízio Liberado
                </h4>
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Ao adicionar este rodízio, todos os produtos da categoria{' '}
                  <strong>{combo.rodizio_category?.name || 'selecionada'}</strong> serão liberados
                  com preço R$ 0,00.
                </p>
              </div>
            </div>
          )}

          <Separator className="my-4" />

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Observações</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Sem cebola, bem passado..."
              className="resize-none"
              rows={2}
            />
          </div>
        </ScrollArea>

        {/* Footer with quantity and add button */}
        <div className="p-4 border-t bg-background">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="font-medium text-lg w-8 text-center">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity((q) => q + 1)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-xl font-bold text-primary">
                {formatCurrency(calculatedPrice * quantity)}
              </p>
            </div>
          </div>
          <Button
            className="w-full"
            size="lg"
            onClick={handleAddToCart}
            disabled={!isValid}
          >
            Adicionar ao pedido
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
